import React from 'react';
import { Container, Typography, Box, Paper, Divider, useTheme, useMediaQuery } from '@mui/material';

const CookiePolicyPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Box sx={{ py: { xs: 4, md: 8 } }}>
      <Container maxWidth="md">
        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            fontWeight="bold"
            sx={{ fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.125rem' } }}
          >
            Cookie Policy
          </Typography>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 3, md: 4 },
              fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' }
            }}
          >
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            This Cookie Policy explains how roamzap("we", "us", or "our") uses cookies and similar technologies 
            to recognize you when you visit our website. It explains what these technologies are and why we use them, 
            as well as your rights to control our use of them.
          </Typography>
          
          <Typography 
            variant="h6" 
            gutterBottom 
            fontWeight="bold" 
            sx={{ 
              mt: { xs: 3, md: 4 },
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' }
            }}
          >
            What Are Cookies?
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            Cookies are small data files that are placed on your computer or mobile device when you visit a website. 
            Cookies are widely used by website owners to make their websites work, or to work more efficiently, 
            as well as to provide reporting information.
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            Cookies set by the website owner (in this case, roamzap) are called "first-party cookies". 
            Cookies set by parties other than the website owner are called "third-party cookies". 
            Third-party cookies enable third-party features or functionality to be provided on or through the website 
            (e.g., advertising, interactive content, and analytics).
          </Typography>
          
          <Typography 
            variant="h6" 
            gutterBottom 
            fontWeight="bold" 
            sx={{ 
              mt: { xs: 3, md: 4 },
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' }
            }}
          >
            Why Do We Use Cookies?
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            We use first-party and third-party cookies for several reasons. Some cookies are required for technical reasons 
            in order for our website to operate, and we refer to these as "essential" or "strictly necessary" cookies. 
            Other cookies also enable us to track and target the interests of our users to enhance the experience on our website. 
            Third parties serve cookies through our website for analytics and other purposes.
          </Typography>
          
          <Typography 
            variant="h6" 
            gutterBottom 
            fontWeight="bold" 
            sx={{ 
              mt: { xs: 3, md: 4 },
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' }
            }}
          >
            Types of Cookies We Use
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            The specific types of first and third-party cookies served through our website and the purposes they perform include:
          </Typography>
          
          <ul style={{ paddingLeft: isMobile ? '20px' : '40px' }}>
            <li>
              <Typography 
                paragraph
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                  mt: 0.5,
                  mb: { xs: 1, md: 2 }
                }}
              >
                <strong>Essential Cookies:</strong> These cookies are strictly necessary to provide you with services available through our website 
                and to use some of its features, such as access to secure areas.
              </Typography>
            </li>
            <li>
              <Typography 
                paragraph
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                  mt: 0.5,
                  mb: { xs: 1, md: 2 }
                }}
              >
                <strong>Performance/Analytics Cookies:</strong> These cookies allow us to count visits and traffic sources so we can measure 
                and improve the performance of our site. They help us to know which pages are the most and least popular and see how visitors 
                move around the site.
              </Typography>
            </li>
            <li>
              <Typography 
                paragraph
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                  mt: 0.5,
                  mb: { xs: 1, md: 2 }
                }}
              >
                <strong>Functionality Cookies:</strong> These cookies enable the website to provide enhanced functionality and personalization. 
                They may be set by us or by third-party providers whose services we have added to our pages.
              </Typography>
            </li>
            <li>
              <Typography 
                paragraph
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                  mt: 0.5,
                  mb: { xs: 1, md: 2 }
                }}
              >
                <strong>Targeting Cookies:</strong> These cookies may be set through our site by our advertising partners. They may be used by those 
                companies to build a profile of your interests and show you relevant advertisements on other sites.
              </Typography>
            </li>
          </ul>
          
          <Typography 
            variant="h6" 
            gutterBottom 
            fontWeight="bold" 
            sx={{ 
              mt: { xs: 3, md: 4 },
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' }
            }}
          >
            How Can You Control Cookies?
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            You have the right to decide whether to accept or reject cookies. You can exercise your cookie rights by setting your preferences 
            in the Cookie Consent Manager that we provide on our website.
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            Browser controls: Most browsers also allow you to control cookies through their settings. Please note that if you choose to reject cookies, 
            you may not be able to use all the features of our website.
          </Typography>
          
          <Typography 
            variant="h6" 
            gutterBottom 
            fontWeight="bold" 
            sx={{ 
              mt: { xs: 3, md: 4 },
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' }
            }}
          >
            Changes to This Cookie Policy
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            We may update this Cookie Policy from time to time in order to reflect, for example, changes to the cookies we use or for other operational, 
            legal or regulatory reasons. Please therefore re-visit this Cookie Policy regularly to stay informed about our use of cookies and related technologies.
          </Typography>
          
          <Divider sx={{ my: { xs: 3, md: 4 } }} />
        </Paper>
      </Container>
    </Box>
  );
};

export default CookiePolicyPage; 